import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  Modal,
  Platform,
  ScrollView,
  Switch,
  TextInput,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../store';
import { useToast } from '../../../../components/LhToastContainer';
import { AdminColors } from '../../constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import AdminButton from '../../components/AdminButton';
import AdminInfoBanner from '../../components/AdminInfoBanner';
import { AdminFormField, AdminTextInput } from '../../components/AdminFormField';
import LocationFormSelect from './LocationFormSelect';
import LocationUserSearchSelect from './LocationUserSearchSelect';
import { getUserDisplayName } from './LocationUserSearchSelect';
import { styles as profileStyles } from './LocationProfileModal.styles';
import { confirmStyles, panelStyles } from './locationModalStyles';
import { locationApi } from './locationApi';
import { Location, LocationFormValues, LOCATION_FACILITY_TYPE_OPTIONS } from './locationTypes';
import AddRouteModal from './AddRouteModal';
import BulkAddRoutesModal from './BulkAddRoutesModal';
import RouteDataTable from '../RouteManagement/RouteDataTable';
import { createApiClient, getLocalTimestamp, getTimezoneOffsetString } from '../../../../api/apiClient';
import { fetchActiveRouteTypes, fetchRoutes, toggleRouteActive, updateRouteType } from '../RouteManagement/routeManagementApi';
import { Route as ManagedRoute, ROUTE_TYPE_OPTIONS } from '../RouteManagement/routeManagementTypes';
import { useTheme } from '../../../../theme/useTheme';

export type LocationModalMode = 'profile' | 'add' | 'edit' | 'bulk';
type LocationTabId = 'information' | 'employees' | 'routes';
type UniqueField = 'id' | 'sap';
type UniqueCheckStatus = 'idle' | 'checking' | 'unique' | 'duplicate' | 'error';

export interface Employee {
  name?: string;
  employeeId?: string;
  accessLevel?: string;
  lastLogin?: string;
  status?: string;
  [key: string]: unknown;
}

export interface Route {
  number: string;
  type: string;
  active: boolean;
}

export interface LocationManagementModalProps {
  visible: boolean;
  mode: LocationModalMode;
  location?: Location | null;
  selectedLocations?: Location[];
  employees?: Employee[];
  routes?: Route[];
  duoEnabled?: boolean;
  onClose: () => void;
  onBack?: () => void;
  onShowOnMap?: () => void;
  onEditLocation?: () => void;
  onSave?: (location: Location) => void;
  onBulkApply?: (updatedIds: string[], failedCount: number) => void;
  onDuoToggle?: (value: boolean) => void;
  onToast?: (message: string) => void;
  existingIds?: string[];
  existingSapIds?: string[];
  hideEditLocation?: boolean;
  edfSynced?: boolean;
  edfSyncTimestamp?: string;
  onModeChange?: (mode: LocationModalMode) => void;
}

type FieldType = 'text' | 'number' | 'integer';

interface FieldDef {
  key: keyof LocationFormValues;
  label: string;
  required?: boolean;
  readOnly?: boolean;
  readonlyEdit?: boolean;
  edfLocked?: boolean;
  bulkExcluded?: boolean;
  optional?: boolean;
  type?: FieldType;
  options?: string[] | 'regions' | 'markets' | 'users';
  placeholder?: string;
  span?: 2;
  max?: number;
}

interface SectionDef {
  key: string;
  title: string;
  fields: FieldDef[];
}

const PROFILE_SECTION_STATE_KEY = 'navigator.locationManagement.profileSectionState.v1';
const FORM_SECTION_STATE_KEY = 'navigator.locationManagement.formSectionState.v1';
const KEEP_VALUE = '__KEEP__';
const LOC_ID_UNIQUE_ERROR = 'LOC ID already exists. Please enter a unique Location ID.';
const SAP_ID_UNIQUE_ERROR = 'SAP ID already exists. Please enter a unique SAP ID.';
const ALPHANUMERIC_RE = /^[A-Za-z0-9]*$/;
const YEAR_BUILT_RE = /^\d{4}$/;
const INTEGER_RE = /^\d+$/;
const SQL_INT_MAX = 2147483647;
const LAT_LONG_RE = /^-?\d+(?:\.\d{1,6})?$/;
const DECIMAL_18_2_RE = /^\d{1,16}(?:\.\d{1,2})?$/;
const DECIMAL_18_2_MAX = 9999999999999999.99;
const INTEGER_FIELD_KEYS: Array<keyof LocationFormValues> = ['yearBuilt', 'docks', 'docksEng', 'docksNoEng', 'driveIn', 'bottleLines', 'canLines'];
const EXECUTION_SYSTEMS = ['EWM', 'Voice Pick', 'Vision Pick (WES)', 'WMS'];
const WAREHOUSE_SYSTEMS = ['WMS', 'WES - Vision', 'Not Assigned'];
const OWNERSHIP_OPTIONS = ['Owned', 'Leased', 'Op Agreement', 'Unknown'];
const DEPT_OPTIONS = ['Distribution', 'Production', 'Manufacturing'];
const ORG_OPTIONS = ['PBNA', 'FLNA', '1NA'];
const TPL_OPTIONS = ['Yes', 'No'];
const SIZE_OPTIONS = ['Small', 'Medium', 'Large'];
const LABOR_OPTIONS = ['Pepsi', '3PL', 'Mixed', 'Unknown'];
const UNION_OPTIONS = ['Union', 'Non-Union', 'Unknown'];
const STATUS_OPTIONS = ['Active', 'Disabled'];
const EDF_GOVERNED = new Set(['id', 'sap', 'name', 'status', 'type', 'org', 'address', 'city', 'state', 'zip', 'latitude', 'longitude', 'region', 'market', 'ownership', 'yearBuilt', 'union']);
const LOCATION_TABS: Array<{ id: LocationTabId; label: string }> = [
  { id: 'information', label: 'Location Information' },
  { id: 'employees', label: 'Active Employees' },
  { id: 'routes', label: 'Route Management' },
];

const LOCATION_SECTIONS: SectionDef[] = [
  {
    key: 'primary',
    title: 'Primary Information',
    fields: [
      { key: 'id', label: 'Location ID', required: true, readonlyEdit: true, bulkExcluded: true, edfLocked: true, max: 5 },
      { key: 'sap', label: 'Location SAP ID', required: true, readonlyEdit: true, bulkExcluded: true, edfLocked: true, max: 4 },
      { key: 'name', label: 'Location Name', required: true, bulkExcluded: true, edfLocked: true, max: 100 },
      { key: 'status', label: 'Location Status', options: STATUS_OPTIONS, placeholder: 'Select status', bulkExcluded: true, edfLocked: true },
    ],
  },
  {
    key: 'classification',
    title: 'Facility Classification',
    fields: [
      { key: 'facilityType', label: 'Location Type', options: LOCATION_FACILITY_TYPE_OPTIONS, placeholder: 'Select location type', required: true },
      { key: 'type', label: 'Department', options: DEPT_OPTIONS, placeholder: 'Select department', required: true, edfLocked: true },
      { key: 'org', label: 'Organization Type', options: ORG_OPTIONS, placeholder: 'Select organization', required: true, edfLocked: true },
      { key: 'tpl', label: '3PL Status', options: TPL_OPTIONS, placeholder: 'Select', required: true },
      { key: 'size', label: 'Location Size', options: SIZE_OPTIONS, placeholder: 'Select size', required: true },
      { key: 'ownership', label: 'Ownership', options: OWNERSHIP_OPTIONS, placeholder: 'Select ownership' },
      { key: 'yearBuilt', label: 'Year Built', type: 'integer' },
    ],
  },
  {
    key: 'geography',
    title: 'Address and Geography',
    fields: [
      { key: 'address', label: 'Street Address', bulkExcluded: true, edfLocked: true, span: 2 },
      { key: 'city', label: 'City', bulkExcluded: true, edfLocked: true },
      { key: 'state', label: 'State / Province', bulkExcluded: true, edfLocked: true },
      { key: 'zip', label: 'ZIP / Postal Code', bulkExcluded: true, edfLocked: true },
      { key: 'latitude', label: 'Latitude', type: 'number', bulkExcluded: true, edfLocked: true },
      { key: 'longitude', label: 'Longitude', type: 'number', bulkExcluded: true, edfLocked: true },
      { key: 'region', label: 'Region', options: 'regions', placeholder: 'Select region', required: true, bulkExcluded: true, edfLocked: true },
      { key: 'market', label: 'Market', options: 'markets', placeholder: 'Select market', required: true, bulkExcluded: true, edfLocked: true },
    ],
  },
  {
    key: 'resources',
    title: 'Resource Assignment',
    fields: [
      { key: 'marketLead', label: 'Market Lead', optional: true, options: 'users' },
      { key: 'regionLead', label: 'Region Lead', optional: true, options: 'users' },
      { key: 'coe', label: 'Capability Resource', optional: true, options: 'users' },
      { key: 'coe2', label: 'E2E Resource', optional: true, options: 'users' },
    ],
  },
  {
    key: 'systems',
    title: 'Systems and Labor',
    fields: [
      { key: 'executionSystem', label: 'Execution System', options: EXECUTION_SYSTEMS, placeholder: 'Select execution system' },
      { key: 'warehouseSystem', label: 'Warehouse System', options: WAREHOUSE_SYSTEMS, placeholder: 'Select warehouse system' },
      { key: 'labor', label: 'Labor Model', options: LABOR_OPTIONS, placeholder: 'Select labor model' },
      { key: 'union', label: 'Union Status', options: UNION_OPTIONS, placeholder: 'Select union status', edfLocked: true },
      { key: 'supplier', label: '3PL Supplier', optional: true },
    ],
  },
  {
    key: 'capacity',
    title: 'Capacity and Scale',
    fields: [
      { key: 'totalSqft', label: 'Total Building Sqft', type: 'number' },
      { key: 'operationalSqft', label: 'Operational Sqft', type: 'number' },
      { key: 'pickedVolume', label: 'Picked Volume', type: 'number', bulkExcluded: true, readonlyEdit: true },
    ],
  },
  {
    key: 'docks',
    title: 'Docks and Production',
    fields: [
      { key: 'docks', label: 'Total Docks', type: 'integer' },
      { key: 'docksEng', label: 'Docks with ENG Controls', type: 'integer' },
      { key: 'docksNoEng', label: 'Docks without ENG Controls', type: 'integer' },
      { key: 'driveIn', label: 'Drive-in Bays', type: 'integer' },
      { key: 'bottleLines', label: 'Bottle Lines', type: 'integer' },
      { key: 'canLines', label: 'Can Lines', type: 'integer' },
    ],
  },
  {
    key: 'network',
    title: 'Network Relationships',
    fields: [
      { key: 'build1', label: 'Crossdock Build Site 1', optional: true },
      { key: 'build2', label: 'Crossdock Build Site 2', optional: true },
      { key: 'bulkSite', label: 'Centralized Bulk Site', optional: true },
    ],
  },
];

const EMPTY_FORM: LocationFormValues = {
  id: '',
  sap: '',
  name: '',
  status: '',
  facilityType: '',
  type: '',
  org: '',
  tpl: '',
  size: '',
  ownership: '',
  yearBuilt: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  latitude: '',
  longitude: '',
  region: '',
  market: '',
  marketLead: '',
  regionLead: '',
  coe: '',
  coe2: '',
  executionSystem: '',
  warehouseSystem: '',
  labor: '',
  union: '',
  supplier: '',
  totalSqft: '',
  operationalSqft: '',
  pickedVolume: '',
  docks: '',
  docksEng: '',
  docksNoEng: '',
  driveIn: '',
  bottleLines: '',
  canLines: '',
  build1: '',
  build2: '',
  bulkSite: '',
};

interface NavigatorUser {
  RoleID?: number | string;
  roleId?: number | string;
  roleID?: number | string;
  First_Name?: string;
  Last_Name?: string;
  Name?: string;
  UserEmail?: string;
  Role?: string;
  role?: string;
  AccessLevel?: string;
  employeeId?: string;
  EmployeeId?: string;
  gpid?: string;
  GPID?: string;
  lastLogin?: string;
  lastLoggedAt?: string;
  Last_Login?: string;
  LastLoggedAt?: string;
  [key: string]: unknown;
}

const getStorageItem = async (key: string): Promise<string | null> => {
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    return localStorage.getItem(key);
  }
  try {
    const AsyncStorage = require('@react-native-async-storage/async-storage').default;
    return await AsyncStorage.getItem(key);
  } catch {
    return null;
  }
};

const setStorageItem = async (key: string, value: string): Promise<void> => {
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    localStorage.setItem(key, value);
    return;
  }
  try {
    const AsyncStorage = require('@react-native-async-storage/async-storage').default;
    await AsyncStorage.setItem(key, value);
  } catch {
    // no-op
  }
};

const locationToForm = (location: Location): LocationFormValues => ({
  id: location.id,
  sap: location.sap,
  name: location.name,
  status: location.status,
  facilityType: location.facilityType ?? '',
  type: location.type,
  org: location.org,
  tpl: location.tpl,
  size: location.size,
  ownership: location.ownership ?? '',
  yearBuilt: location.yearBuilt ?? '',
  address: location.address ?? '',
  city: location.city ?? '',
  state: location.state ?? '',
  zip: location.zip ?? '',
  latitude: location.latitude ?? '',
  longitude: location.longitude ?? '',
  region: location.region,
  market: location.market,
  marketLead: location.marketLead,
  regionLead: location.regionLead,
  coe: location.coe,
  coe2: location.coe2,
  executionSystem: location.executionSystem,
  warehouseSystem: location.warehouseSystem ?? '',
  labor: location.labor ?? '',
  union: location.union ?? '',
  supplier: location.supplier ?? '',
  totalSqft: location.totalSqft ?? '',
  operationalSqft: location.operationalSqft ?? '',
  pickedVolume: location.pickedVolume ?? '',
  docks: location.docks ?? '',
  docksEng: location.docksEng ?? '',
  docksNoEng: location.docksNoEng ?? '',
  driveIn: location.driveIn ?? '',
  bottleLines: location.bottleLines ?? '',
  canLines: location.canLines ?? '',
  build1: location.build1 ?? '',
  build2: location.build2 ?? '',
  bulkSite: location.bulkSite ?? '',
});

const formatProfileValue = (key: keyof LocationFormValues, value: string): string => {
  if (!value) return '—';
  if (key === 'latitude' || key === 'longitude' || key === 'yearBuilt') return value;
  if (INTEGER_FIELD_KEYS.includes(key)) {
    const num = Number(value);
    return Number.isFinite(num) ? Math.round(num).toLocaleString('en-US') : value;
  }
  return value;
};

const validateForm = (
  form: LocationFormValues,
  mode: LocationModalMode,
  existingIds: string[],
  existingSapIds: string[],
  isEdfSynced: boolean,
): Partial<Record<keyof LocationFormValues, string>> => {
  const errors: Partial<Record<keyof LocationFormValues, string>> = {};

  const validateOptionalDecimal18_2 = (key: keyof LocationFormValues, label: string) => {
    const rawValue = String(form[key] ?? '').trim();
    if (!rawValue) return;

    if (!DECIMAL_18_2_RE.test(rawValue)) {
      errors[key] = `${label} must be a non-negative decimal with up to 16 digits before and 2 digits after the decimal.`;
      return;
    }

    const numericValue = Number(rawValue);
    if (Number.isNaN(numericValue) || numericValue < 0 || numericValue > DECIMAL_18_2_MAX) {
      errors[key] = `${label} must be between 0 and ${DECIMAL_18_2_MAX.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.`;
    }
  };

  const validateOptionalInteger = (key: keyof LocationFormValues, label: string) => {
    const rawValue = String(form[key] ?? '').trim();
    if (!rawValue) return;

    if (!INTEGER_RE.test(rawValue)) {
      errors[key] = `${label} must be a whole number.`;
      return;
    }

    const numericValue = Number(rawValue);
    if (Number.isNaN(numericValue) || numericValue < 0 || !Number.isInteger(numericValue)) {
      errors[key] = `${label} must be a whole number.`;
      return;
    }

    if (numericValue > SQL_INT_MAX) {
      errors[key] = `${label} must be less than or equal to ${SQL_INT_MAX.toLocaleString('en-US')}.`;
    }
  };

  if (mode === 'add') {
    if (!form.id.trim()) errors.id = 'Location ID is required.';
    else if (!ALPHANUMERIC_RE.test(form.id)) errors.id = 'Location ID must be alphanumeric.';
    else if (form.id.length > 5) errors.id = 'Location ID must be at most 5 characters.';
    else if (existingIds.includes(form.id.trim().toUpperCase())) errors.id = 'Location ID already exists.';

    if (!form.sap.trim()) errors.sap = 'SAP ID is required.';
    else if (!ALPHANUMERIC_RE.test(form.sap)) errors.sap = 'SAP ID must be alphanumeric.';
    else if (form.sap.length > 4) errors.sap = 'SAP ID must be at most 4 characters.';
    else if (existingSapIds.includes(form.sap.trim())) errors.sap = 'SAP ID already exists.';
  }

  if (mode !== 'bulk') {
    if (!form.name.trim()) errors.name = 'Location Name is required.';
    if (!form.facilityType) errors.facilityType = 'Location Type is required.';
    else if (!LOCATION_FACILITY_TYPE_OPTIONS.includes(form.facilityType)) errors.facilityType = 'Invalid Location Type.';
    if (!form.type) errors.type = 'Department is required.';
    if (!form.org) errors.org = 'Organization Type is required.';
    if (!form.tpl) errors.tpl = '3PL Status is required.';
    if (!form.size) errors.size = 'Location Size is required.';
    if (!isEdfSynced) {
      if (!form.region.trim()) errors.region = 'Region is required.';
      if (!form.market.trim()) errors.market = 'Market is required.';
    }
  }

  const yearBuilt = form.yearBuilt.trim();
  if (yearBuilt) {
    if (!YEAR_BUILT_RE.test(yearBuilt)) {
      errors.yearBuilt = 'Year Built must be exactly 4 digits.';
    } else if (Number(yearBuilt) < 1900) {
      errors.yearBuilt = 'Year Built must be 1900 or later.';
    }
  }

  const latitudeValue = form.latitude.trim();
  if (latitudeValue) {
    if (!LAT_LONG_RE.test(latitudeValue)) {
      errors.latitude = 'Latitude must be a valid number with up to 6 decimal places.';
    } else {
      const latitude = Number(latitudeValue);
      if (Number.isNaN(latitude) || latitude < -90 || latitude > 90) {
        errors.latitude = 'Latitude must be between -90 and 90.';
      }
    }
  }

  const longitudeValue = form.longitude.trim();
  if (longitudeValue) {
    if (!LAT_LONG_RE.test(longitudeValue)) {
      errors.longitude = 'Longitude must be a valid number with up to 6 decimal places.';
    } else {
      const longitude = Number(longitudeValue);
      if (Number.isNaN(longitude) || longitude < -180 || longitude > 180) {
        errors.longitude = 'Longitude must be between -180 and 180.';
      }
    }
  }

  validateOptionalDecimal18_2('operationalSqft', 'Operational Sqft');
  validateOptionalDecimal18_2('totalSqft', 'Total Building Sqft');
  validateOptionalDecimal18_2('pickedVolume', 'Picked Volume');

  validateOptionalInteger('yearBuilt', 'Year Built');
  validateOptionalInteger('docks', 'Total Docks');
  validateOptionalInteger('docksEng', 'Docks with ENG Controls');
  validateOptionalInteger('docksNoEng', 'Docks without ENG Controls');
  validateOptionalInteger('driveIn', 'Drive-in Bays');
  validateOptionalInteger('bottleLines', 'Bottle Lines');
  validateOptionalInteger('canLines', 'Can Lines');

  return errors;
};

const getUserRoleId = (user: NavigatorUser): number => {
  const value = user.RoleID ?? user.roleId ?? user.roleID;
  return Number(value ?? 0);
};

const getEmployeeDisplayName = (employee: Employee): string => {
  const name = employee.name ?? getUserDisplayName(employee as NavigatorUser);
  if (name && name.trim()) return name.trim();
  const fallback = employee.UserEmail ?? employee.employeeId ?? employee.EmployeeId ?? employee.gpid ?? employee.GPID;
  return typeof fallback === 'string' && fallback.trim() ? fallback.trim() : '-';
};

const getEmployeeId = (employee: Employee): string => {
  const value = employee.employeeId ?? employee.EmployeeId ?? employee.gpid ?? employee.GPID ?? employee.UserEmail;
  return typeof value === 'string' && value.trim() ? value.trim() : '-';
};

const getEmployeeAccessLevel = (employee: Employee): string => {
  const value = employee.accessLevel ?? employee.AccessLevel ?? employee.Role ?? employee.role;
  return typeof value === 'string' && value.trim() ? value.trim() : '-';
};

const LAST_LOGIN_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: '2-digit',
  day: '2-digit',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

const getEmployeeLastLoginValue = (employee: Employee): unknown => (
  employee.lastLogin ?? employee.lastLoggedAt ?? employee.Last_Login ?? employee.LastLoggedAt
);

const getEmployeeLastLoginDate = (employee: Employee): Date | null => {
  const value = getEmployeeLastLoginValue(employee);
  if (value == null) return null;

  const date = value instanceof Date ? value : new Date(String(value));
  return Number.isFinite(date.getTime()) ? date : null;
};

const getEmployeeLastLoginTimestamp = (employee: Employee): number => {
  const date = getEmployeeLastLoginDate(employee);
  return date ? date.getTime() : 0;
};

const getEmployeeLastLogin = (employee: Employee): string => {
  const date = getEmployeeLastLoginDate(employee);
  if (date) return LAST_LOGIN_FORMATTER.format(date);

  const fallback = getEmployeeLastLoginValue(employee);
  if (typeof fallback === 'string' && fallback.trim()) return fallback.trim();
  return '-';
};

const AccessLevelBadge: React.FC<{ level?: string }> = ({ level }) => {
  const resolvedLevel = level?.trim() || '-';
  const normalizedLevel = resolvedLevel.toLowerCase();
  const backgroundColor = normalizedLevel === 'manager' ? '#034895' : normalizedLevel === 'supervisor' ? '#005CBC' : '#616161';
  return (
    <View style={[profileStyles.accessBadge, { backgroundColor }]}>
      <Text style={profileStyles.accessBadgeText}>{resolvedLevel}</Text>
    </View>
  );
};

const getUniqueFieldErrorMessage = (field: UniqueField): string => (field === 'id' ? LOC_ID_UNIQUE_ERROR : SAP_ID_UNIQUE_ERROR);

const getUniqueFieldLabel = (field: UniqueField): string => (field === 'id' ? 'LOC ID' : 'SAP ID');

const buildUniquenessServiceError = (field: UniqueField, error: unknown): string => {
  const detail = error instanceof Error ? error.message : '';
  return detail
    ? `Unable to verify ${getUniqueFieldLabel(field)} uniqueness. ${detail}`
    : `Unable to verify ${getUniqueFieldLabel(field)} uniqueness. Please try again.`;
};

const LocationManagementModal: React.FC<LocationManagementModalProps> = ({
  visible,
  mode,
  location,
  selectedLocations = [],
  employees = [],
  routes = [],
  duoEnabled,
  onClose,
  onBack,
  onShowOnMap,
  onEditLocation,
  onSave,
  onBulkApply,
  onDuoToggle,
  onToast,
  existingIds = [],
  existingSapIds = [],
  hideEditLocation = false,
  edfSynced,
  edfSyncTimestamp,
  onModeChange,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
  // Unlike `isDark`, true on web too: used for the tab bar, whose active state is
  // otherwise swallowed by the global dark sweep (active and inactive look alike).
  const isDarkAny = resolvedTheme === 'dark';
  const [form, setForm] = useState<LocationFormValues>(EMPTY_FORM);
  const [initialForm, setInitialForm] = useState<LocationFormValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof LocationFormValues, string>>>({});
  const [uniquenessErrors, setUniquenessErrors] = useState<Partial<Record<UniqueField, string>>>({});
  const [uniquenessStatus, setUniquenessStatus] = useState<Record<UniqueField, UniqueCheckStatus>>({
    id: 'idle',
    sap: 'idle',
  });
  const [validationServiceError, setValidationServiceError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [displayLocation, setDisplayLocation] = useState<Location | null>(location ?? null);
  const [showDiscard, setShowDiscard] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [regions, setRegions] = useState<string[]>([]);
  const [markets, setMarkets] = useState<string[]>([]);
  const [activeProfileTab, setActiveProfileTab] = useState<'information' | 'employees' | 'routes'>('information');
  const [activeEditFormTab, setActiveEditFormTab] = useState<LocationTabId>('information');
  const [employeeSortAscending, setEmployeeSortAscending] = useState(false);
  const [profileCollapsed, setProfileCollapsed] = useState<Record<string, boolean>>({});
  const [formCollapsed, setFormCollapsed] = useState<Record<string, boolean>>({});
  const slideAnim = useMemo(() => new Animated.Value(0), []);
  const { width: viewportWidth, height: viewportHeight } = useWindowDimensions();
  const uniquenessRequestSeqRef = useRef<Record<UniqueField, number>>({ id: 0, sap: 0 });
  const uniquenessCacheRef = useRef<Partial<Record<UniqueField, { value: string; isUnique: boolean }>>>({});
  const uniquenessFlightRef = useRef<Partial<Record<UniqueField, { value: string; requestId: number; promise: Promise<boolean> }>>>({});

  const isCompactLayout = viewportWidth < 900;
  const panelWidth = isCompactLayout ? viewportWidth : Math.round(viewportWidth * 0.7);

  const fieldUsers = useSelector((state: RootState) => (state.appData?.FieldUsers ?? []) as NavigatorUser[]);
  const coeUsers = useSelector((state: RootState) => (state.appData?.COEUsers ?? []) as NavigatorUser[]);
  const toast = useToast();
  const requestedBy = useSelector((state: RootState) => state.appData?.userInfo?.email ?? (state as any).auth?.user?.email ?? (state as any).auth?.user?.username ?? '');

  const allUsers = useMemo(() => [...fieldUsers, ...coeUsers], [fieldUsers, coeUsers]);
  const marketLeadUsers = useMemo(() => allUsers.filter(user => getUserRoleId(user) === 3), [allUsers]);
  const coeResourceUsers = useMemo(() => allUsers.filter(user => getUserRoleId(user) === 2), [allUsers]);

  const isProfileMode = mode === 'profile';
  const isEditMode = mode === 'edit';
  const isAddMode = mode === 'add';
  const isBulkMode = mode === 'bulk';
  const resolvedEdfSynced = Boolean((typeof edfSynced === 'boolean' ? edfSynced : location?.edfSynced) ?? false);
  const bulkHasSynced = isBulkMode && selectedLocations.some(item => item.edfSynced);
  const [duoOverride, setDuoOverride] = useState<boolean | null>(null);
  const duoState = duoOverride ?? (typeof duoEnabled === 'boolean' ? duoEnabled : Boolean(location?.isDuo));
  const [routeRows, setRouteRows] = useState<ManagedRoute[]>([]);
  const [routeIsLoading, setRouteIsLoading] = useState(false);
  const [routeError, setRouteError] = useState<string | null>(null);
  const [routeSearch, setRouteSearch] = useState('');
  const [routeTotalPages, setRouteTotalPages] = useState(0);
  const [routeTotalCount, setRouteTotalCount] = useState(0);
  const [routeIsFirst, setRouteIsFirst] = useState(true);
  const [routeIsLast, setRouteIsLast] = useState(false);
  const [routePage, setRoutePage] = useState(0);
  const [routeTypeOptions, setRouteTypeOptions] = useState<{ label: string; value: string }[]>(ROUTE_TYPE_OPTIONS);
  const [routeLoadParams, setRouteLoadParams] = useState({
    page: 0,
    size: 10,
    sort: 'routeNumber,asc',
    searchTerm: '',
    routeTypes: undefined as string[] | undefined,
    isActiveList: undefined as boolean[] | undefined,
  });
  const [routeColumnFilters, setRouteColumnFilters] = useState<Record<string, string[]>>({});
  const [showAddRouteModal, setShowAddRouteModal] = useState(false);
  const [showBulkAddRoutesModal, setShowBulkAddRoutesModal] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeEmployees = useMemo(
    () => employees.filter(employee => employee.status == null || employee.status !== 'Inactive'),
    [employees],
  );
  const hasMapCoordinates = useMemo(() => {
    if (!location) return false;
    const latitude = Number(location.latitude);
    const longitude = Number(location.longitude);
    return Number.isFinite(latitude) && Number.isFinite(longitude);
  }, [location]);
  const sortedEmployees = useMemo(() => {
    const clone = [...activeEmployees];
    clone.sort((left, right) => {
      const leftValue = getEmployeeLastLoginTimestamp(left);
      const rightValue = getEmployeeLastLoginTimestamp(right);
      return employeeSortAscending ? leftValue - rightValue : rightValue - leftValue;
    });
    return clone;
  }, [activeEmployees, employeeSortAscending]);

  const formIsDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initialForm), [form, initialForm]);
  const sectionStorageKey = isProfileMode ? PROFILE_SECTION_STATE_KEY : FORM_SECTION_STATE_KEY;

  useEffect(() => {
    if (!visible) return;
    slideAnim.setValue(0);
    Animated.timing(slideAnim, {
      toValue: 1,
      duration: 220,
      useNativeDriver: true,
    }).start();

    setSubmitted(false);
    setSaving(false);
    setApiError(null);
    setValidationServiceError(null);
    setShowDiscard(false);
    setShowDisableConfirm(false);
    setErrors({});
    setUniquenessErrors({});
    setUniquenessStatus({ id: 'idle', sap: 'idle' });
    setActiveProfileTab('information');
    setActiveEditFormTab('information');
    setEmployeeSortAscending(false);
    setDuoOverride(null);
    setRouteError(null);
    setRouteSearch('');
    setRouteColumnFilters({});
    setRouteLoadParams({
      page: 0,
      size: 10,
      sort: 'routeNumber,asc',
      searchTerm: '',
      routeTypes: undefined,
      isActiveList: undefined,
    });
    setRoutePage(0);
    setRouteTotalPages(0);
    setRouteTotalCount(0);
    setRouteIsFirst(true);
    setRouteIsLast(false);
    setRouteRows([]);
    setShowAddRouteModal(false);
    setShowBulkAddRoutesModal(false);
    uniquenessRequestSeqRef.current = { id: 0, sap: 0 };
    uniquenessCacheRef.current = {};
    uniquenessFlightRef.current = {};

    const shouldSyncDisplayLocation =
      location && (
        displayLocation == null ||
        displayLocation.id !== location.id
      );

    if ((isEditMode || isProfileMode) && location) {
      const nextForm = locationToForm(location);
      setForm(nextForm);
      setInitialForm(nextForm);
      if (shouldSyncDisplayLocation) {
        setDisplayLocation(location);
      }
    } else if (isAddMode || isBulkMode) {
      setForm(EMPTY_FORM);
      setInitialForm(EMPTY_FORM);
      setDisplayLocation(null);
    }
  }, [displayLocation, visible, mode, location, isEditMode, isProfileMode, isAddMode, isBulkMode, slideAnim]);

  useEffect(() => {
    if (!visible) return;
    getStorageItem(sectionStorageKey).then(raw => {
      if (!raw) return;
      try {
        const parsed = JSON.parse(raw) as Record<string, boolean>;
        if (isProfileMode) setProfileCollapsed(parsed);
        else setFormCollapsed(parsed);
      } catch {
        // ignore broken persisted state
      }
    });
  }, [visible, sectionStorageKey, isProfileMode]);

  useEffect(() => {
    if (!visible || isProfileMode) return;
    if (!form.region) {
      setMarkets([]);
      return;
    }
    locationApi.getMarketsByRegion(form.region).then(setMarkets).catch(() => setMarkets([]));
  }, [visible, isProfileMode, form.region]);

  useEffect(() => {
    if (!visible || isProfileMode) return;
    locationApi.getRegions().then(setRegions).catch(() => setRegions([]));
  }, [visible, isProfileMode]);

  useEffect(() => {
    if (!visible) return;
    fetchActiveRouteTypes()
      .then(options => {
        if (options.length > 0) setRouteTypeOptions(options);
      })
      .catch(() => undefined);
  }, [visible]);

  useEffect(() => {
    if (!visible || isAddMode || !location?.name) return;

    let cancelled = false;
    (async () => {
      setRouteIsLoading(true);
      setRouteError(null);
      try {
        const response = await fetchRoutes({
          locationName: location.name,
          page: routeLoadParams.page,
          size: routeLoadParams.size,
          sort: routeLoadParams.sort,
          searchTerm: routeLoadParams.searchTerm,
          routeTypes: routeLoadParams.routeTypes,
          isActiveList: routeLoadParams.isActiveList,
        });
        if (cancelled) return;
        setRouteRows(response.data);
        setRoutePage(response.currentPage);
        setRouteTotalPages(response.totalPages);
        setRouteTotalCount(response.totalCount);
        setRouteIsFirst(response.isFirst);
        setRouteIsLast(response.isLast);
      } catch (error: unknown) {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : 'Failed to load routes.';
        setRouteError(message);
        setRouteRows([]);
      } finally {
        if (!cancelled) setRouteIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [visible, isAddMode, location?.name, routeLoadParams]);

  useEffect(() => () => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  }, []);

  const displayedFormSections = useMemo(() => {
    return LOCATION_SECTIONS.map(section => ({
      ...section,
      fields: section.fields.filter(field => {
        if (isBulkMode) {
          if (field.bulkExcluded) return false;
          if (bulkHasSynced && field.edfLocked) return false;
          return true;
        }
        if (isAddMode && field.key === 'status') return false;
        return true;
      }),
    })).filter(section => section.fields.length > 0);
  }, [bulkHasSynced, isAddMode, isBulkMode, isEditMode]);

  const profileSections = LOCATION_SECTIONS;

  const persistCollapsed = useCallback(async (next: Record<string, boolean>) => {
    if (isProfileMode) setProfileCollapsed(next);
    else setFormCollapsed(next);
    await setStorageItem(sectionStorageKey, JSON.stringify(next));
  }, [isProfileMode, sectionStorageKey]);

  const handleToggleSection = useCallback((key: string) => {
    const current = isProfileMode ? profileCollapsed : formCollapsed;
    persistCollapsed({ ...current, [key]: !current[key] }).catch(() => undefined);
  }, [formCollapsed, isProfileMode, persistCollapsed, profileCollapsed]);

  const handleToggleAll = useCallback(() => {
    const current = isProfileMode ? profileCollapsed : formCollapsed;
    const sections = isProfileMode ? profileSections : displayedFormSections;
    const allCollapsed = sections.every(section => current[section.key]);
    const next: Record<string, boolean> = {};
    sections.forEach(section => {
      next[section.key] = !allCollapsed;
    });
    persistCollapsed(next).catch(() => undefined);
  }, [displayedFormSections, formCollapsed, isProfileMode, persistCollapsed, profileCollapsed, profileSections]);

  const deriveRouteApiFilters = useCallback((filters: Record<string, string[]>) => ({
    routeTypes: (filters.routeType ?? []).length > 0 ? filters.routeType : undefined,
    isActiveList: (filters.isActive ?? []).length > 0
      ? filters.isActive.map(value => value === 'true')
      : undefined,
  }), []);

  const handleRouteSearchChange = useCallback((text: string) => {
    setRouteSearch(text);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setRouteLoadParams(prev => ({ ...prev, searchTerm: text.trim(), page: 0 }));
    }, 350);
  }, []);

  const handleRouteColumnFilter = useCallback((key: string, values: string[]) => {
    setRouteColumnFilters(prev => {
      const nextFilters = { ...prev, [key]: values };
      setRouteLoadParams(current => ({ ...current, ...deriveRouteApiFilters(nextFilters), page: 0 }));
      return nextFilters;
    });
  }, [deriveRouteApiFilters]);

  const handleRouteSortChange = useCallback((key: string, direction: 'asc' | 'desc') => {
    setRouteLoadParams(prev => ({ ...prev, sort: `${key},${direction}`, page: 0 }));
  }, []);

  const handleClearRouteFilters = useCallback(() => {
    setRouteSearch('');
    setRouteColumnFilters({});
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    setRouteLoadParams(prev => ({
      ...prev,
      searchTerm: '',
      routeTypes: undefined,
      isActiveList: undefined,
      page: 0,
    }));
  }, []);

  const verifyFieldUniqueness = useCallback(async (field: UniqueField, rawValue: string): Promise<boolean> => {
    const value = rawValue.trim();

    if (!value) {
      setUniquenessErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
      setUniquenessStatus(prev => ({ ...prev, [field]: 'idle' }));
      setValidationServiceError(null);
      return true;
    }

    const cached = uniquenessCacheRef.current[field];
    if (cached?.value === value) {
      setUniquenessErrors(prev => {
        const next = { ...prev };
        if (cached.isUnique) delete next[field];
        else next[field] = getUniqueFieldErrorMessage(field);
        return next;
      });
      setUniquenessStatus(prev => ({ ...prev, [field]: cached.isUnique ? 'unique' : 'duplicate' }));
      if (cached.isUnique) setValidationServiceError(null);
      return cached.isUnique;
    }

    const inFlight = uniquenessFlightRef.current[field];
    if (inFlight?.value === value) {
      return inFlight.promise;
    }

    const requestId = ++uniquenessRequestSeqRef.current[field];
    const promise = (async () => {
      setUniquenessStatus(prev => ({ ...prev, [field]: 'checking' }));
      try {
        const result = await locationApi.checkLocationUniqueness(field === 'id' ? 'loc-id' : 'sap-id', value);
        if (uniquenessRequestSeqRef.current[field] !== requestId) return false;

        uniquenessCacheRef.current[field] = { value, isUnique: result.isUnique };
        if (result.isUnique) {
          setUniquenessErrors(prev => {
            const next = { ...prev };
            delete next[field];
            return next;
          });
          setUniquenessStatus(prev => ({ ...prev, [field]: 'unique' }));
          setValidationServiceError(null);
          return true;
        }

        setUniquenessErrors(prev => ({ ...prev, [field]: getUniqueFieldErrorMessage(field) }));
        setUniquenessStatus(prev => ({ ...prev, [field]: 'duplicate' }));
        return false;
      } catch (error: unknown) {
        if (uniquenessRequestSeqRef.current[field] !== requestId) return false;

        setUniquenessStatus(prev => ({ ...prev, [field]: 'error' }));
        setValidationServiceError(buildUniquenessServiceError(field, error));
        return false;
      } finally {
        if (uniquenessFlightRef.current[field]?.requestId === requestId) {
          delete uniquenessFlightRef.current[field];
        }
      }
    })();

    uniquenessFlightRef.current[field] = { value, requestId, promise };
    return promise;
  }, []);

  const handleUniqueFieldBlur = useCallback(async (field: UniqueField) => {
    if (!isAddMode) return;

    const trimmedValue = String(form[field] ?? '').trim();
    if (trimmedValue !== form[field]) {
      setForm(prev => (prev[field] === trimmedValue ? prev : { ...prev, [field]: trimmedValue }));
    }

    await verifyFieldUniqueness(field, trimmedValue);
  }, [form, isAddMode, verifyFieldUniqueness]);

  const handleToggleDuo = useCallback(async () => {
    if (isProfileMode || !location?.id) return;
    const nextValue = !duoState;
    setDuoOverride(nextValue);
    try {
      const api = createApiClient();
      await api.patch(`/admin/locations/${encodeURIComponent(location.id)}/duo-status`, {
        isDuo: nextValue,
        requestedBy,
      }, {
        params: {
          clientTimestamp: getLocalTimestamp(),
          clientTimezone: getTimezoneOffsetString(),
        },
      });
      onDuoToggle?.(nextValue);
      toast.showToast({
        header: 'Success',
        subtext: nextValue ? 'DUO enabled.' : 'DUO disabled.',
        variant: 'success',
      });
    } catch (error: unknown) {
      setDuoOverride(null);
      const message = error instanceof Error ? error.message : 'Failed to update DUO status.';
      toast.showToast({ header: 'Error', subtext: message, variant: 'error' });
    }
  }, [duoState, isProfileMode, location?.id, onDuoToggle, requestedBy, toast]);

  const refreshRoutes = useCallback(() => {
    setRouteLoadParams(prev => ({ ...prev, page: 0 }));
  }, []);

  const handleFieldChange = useCallback((key: keyof LocationFormValues, value: string) => {
    const nextForm = key === 'region' ? { ...form, [key]: value, market: '' } : { ...form, [key]: value };
    setForm(nextForm);
    if (key === 'id' || key === 'sap') {
      setUniquenessErrors(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setUniquenessStatus(prev => ({ ...prev, [key]: 'idle' }));
      setValidationServiceError(null);
    }
    if (submitted) {
      const nextErrors = validateForm(nextForm, mode, existingIds, existingSapIds, resolvedEdfSynced);
      setErrors(prev => ({ ...prev, [key]: nextErrors[key] }));
    }
  }, [existingIds, existingSapIds, form, mode, resolvedEdfSynced, submitted]);

  const requestClose = useCallback(() => {
    if (!isProfileMode && formIsDirty) {
      setShowDiscard(true);
      return;
    }
    onClose();
  }, [formIsDirty, isProfileMode, onClose]);

  const executeSave = useCallback(async () => {
    setSaving(true);
    setApiError(null);
    try {
      if (isAddMode) {
        const saved = await locationApi.create(form);
        onToast?.('Location created successfully');
        onSave?.(saved);
        onClose();
        return;
      }

      if (isEditMode && location) {
        const saved = await locationApi.updateFull(location.id, form);
        const nextLocation = { ...saved, ...form };
        setDisplayLocation(nextLocation);
        onToast?.('Location updated successfully');
        onSave?.(nextLocation);
        onClose();
        return;
      }

      if (isBulkMode) {
        const ids = selectedLocations.map(item => item.id);
        const excluded = new Set(['id', 'sap', 'name', 'status', 'address', 'city', 'state', 'zip', 'latitude', 'longitude', 'region', 'market']);
        const changes: Partial<LocationFormValues> = {};
        (Object.keys(form) as Array<keyof LocationFormValues>).forEach(key => {
          const value = form[key];
          if (!value || value === KEEP_VALUE || excluded.has(key)) return;
          if (bulkHasSynced && EDF_GOVERNED.has(key)) return;
          changes[key] = value;
        });

        if (Object.keys(changes).length === 0) {
          setApiError('No changes to apply. Populate at least one field.');
          return;
        }

        const result = await locationApi.bulkUpdate(ids, changes);
        const failedCount = result.failedIds?.length ?? 0;
        if (failedCount > 0) {
          setApiError(`${failedCount} location(s) could not be updated:\n${result.failedIds.map(item => `${item.id}: ${item.reason}`).join('\n')}`);
          return;
        }

        const updatedIds = result.updatedIds ?? ids;
        onToast?.(`${updatedIds.length} location${updatedIds.length !== 1 ? 's' : ''} updated successfully`);
        onBulkApply?.(updatedIds, failedCount);
        onClose();
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
      setApiError(message);
    } finally {
      setSaving(false);
    }
  }, [activeEmployees.length, bulkHasSynced, existingIds, existingSapIds, form, form.status, initialForm.status, isAddMode, isBulkMode, isEditMode, location, mode, onBulkApply, onClose, onModeChange, onSave, onToast, resolvedEdfSynced, selectedLocations, verifyFieldUniqueness]);

  const handleSave = useCallback(async () => {
    if (isProfileMode) return;
    setSubmitted(true);
    const nextErrors = validateForm(form, mode, existingIds, existingSapIds, resolvedEdfSynced);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    if (isAddMode) {
      const [locIdUnique, sapIdUnique] = await Promise.all([
        verifyFieldUniqueness('id', form.id),
        verifyFieldUniqueness('sap', form.sap),
      ]);

      if (!locIdUnique || !sapIdUnique) return;
    }

    const activeEmployeeCount = activeEmployees.length;
    if (isEditMode && form.status === 'Disabled' && initialForm.status !== 'Disabled' && activeEmployeeCount > 0) {
      setShowDisableConfirm(true);
      return;
    }

    await executeSave();
  }, [activeEmployees.length, executeSave, existingIds, existingSapIds, form, initialForm.status, isEditMode, isProfileMode, mode, resolvedEdfSynced]);

  const handleConfirmDisable = useCallback(async () => {
    setShowDisableConfirm(false);
    await executeSave();
  }, [executeSave]);

  const renderProfileField = useCallback((field: FieldDef) => {
    const profileLocation = displayLocation ?? location;
    const value = profileLocation ? formatProfileValue(field.key, String(locationToForm(profileLocation)[field.key] ?? '')) : '—';
    const displayValue = value === '—' ? '' : value;
    const wrapStyle = field.span === 2
      ? [panelStyles.formFieldWrap, panelStyles.formFieldSpan2]
      : panelStyles.formFieldWrap;

    return (
      <View key={field.key} style={wrapStyle}>
        <AdminFormField label={field.label}>
          <AdminTextInput
            value={displayValue}
            readOnly
            placeholder="—"
            onChangeText={() => undefined}
          />
        </AdminFormField>
      </View>
    );
  }, [displayLocation, location]);

  const renderProfileSection = useCallback((section: SectionDef) => {
    const collapsed = profileCollapsed[section.key] ?? false;
    return (
      <View key={section.key} style={[panelStyles.formSection, isDark && { borderColor: colors.border.divider }]}>
        <TouchableOpacity
          style={[panelStyles.formSectionHeader, isDark && { backgroundColor: colors.background.elevated, borderBottomColor: colors.border.divider }]}
          onPress={() => handleToggleSection(section.key)}
          activeOpacity={0.75}
        >
          <Text style={[panelStyles.formSectionTitle, isDark && { color: colors.text.primary }]}>{section.title}</Text>
          <Text style={[panelStyles.formSectionChevron, isDark && { color: colors.text.secondary }]}>{collapsed ? '▸' : '▾'}</Text>
        </TouchableOpacity>
        {!collapsed && (
          <View style={[panelStyles.formSectionBody, isDark && { backgroundColor: colors.background.surface }]}>
            <View style={panelStyles.formGrid}>
              {section.fields.map(renderProfileField)}
            </View>
          </View>
        )}
      </View>
    );
  }, [handleToggleSection, isDark, colors, profileCollapsed, renderProfileField]);

  const renderEmployeesSection = useCallback((context: 'profile' | 'edit') => {
    const locationName = (displayLocation ?? location)?.name ?? 'this location';
    const empty = sortedEmployees.length === 0;
    return (
      <View style={[profileStyles.detailSection, isDark && { borderBottomColor: colors.border.divider }]}>
        <View style={profileStyles.subtableHead}>
          <Text style={[profileStyles.subtableTitle, isDark && { color: colors.text.primary }]}>Active Employees with Navi Access</Text>
          <Text style={[profileStyles.subtableSubtitle, isDark && { color: colors.text.secondary }]}>
            {empty ? 'No active employees at this location.' : `${sortedEmployees.length} active employee${sortedEmployees.length !== 1 ? 's' : ''} at this location.`}
          </Text>
        </View>
        {empty ? (
          <View style={profileStyles.emptyState}>
            <Text style={[profileStyles.emptyStateText, isDark && { color: colors.text.secondary }]}>
              No employees currently have Navigator access at {locationName}.
            </Text>
          </View>
        ) : (
          <View style={profileStyles.tableWrap}>
            <View style={[profileStyles.tableHeaderRow, isDark && { backgroundColor: colors.background.elevated, borderBottomColor: colors.border.default }]}>
              <Text style={[profileStyles.tableHeaderCell, profileStyles.colName, isDark && { color: colors.text.secondary }]}>Employee Name</Text>
              <Text style={[profileStyles.tableHeaderCell, profileStyles.colId, isDark && { color: colors.text.secondary }]}>Employee ID</Text>
              <Text style={[profileStyles.tableHeaderCell, profileStyles.colAccess, isDark && { color: colors.text.secondary }]}>Access Level</Text>
              <TouchableOpacity style={profileStyles.colLogin} onPress={() => setEmployeeSortAscending(previous => !previous)} activeOpacity={0.7}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Text style={[profileStyles.tableHeaderCell, isDark && { color: colors.text.secondary }]}>Last Login</Text>
                  <Text style={[profileStyles.tableHeaderCell, { marginLeft: 6 }, isDark && { color: colors.text.secondary }]}>
                    {employeeSortAscending ? '▴' : '▾'}
                  </Text>
                </View>
              </TouchableOpacity>
            </View>
            {sortedEmployees.map((employee, index) => (
              <View key={`${employee.employeeId}-${index}`} style={[profileStyles.tableRow, index === sortedEmployees.length - 1 && profileStyles.tableRowLast, isDark && { borderBottomColor: colors.border.divider }]}>
                <Text style={[profileStyles.tableCell, profileStyles.colName, profileStyles.tableCellBold, isDark && { color: colors.text.primary }]}>{getEmployeeDisplayName(employee)}</Text>
                <Text style={[profileStyles.tableCell, profileStyles.colId, isDark && { color: colors.text.primary }]}>{getEmployeeId(employee)}</Text>
                <View style={profileStyles.colAccess}>
                  <AccessLevelBadge level={getEmployeeAccessLevel(employee)} />
                </View>
                <Text style={[profileStyles.tableCell, profileStyles.colLogin, isDark && { color: colors.text.primary }]}>{getEmployeeLastLogin(employee)}</Text>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  }, [displayLocation, isDark, colors, location?.name, sortedEmployees]);

  const handleRouteTypeChange = useCallback(async (id: number, newType: string) => {
    if (isProfileMode || !location?.id) return;
    const previous = routeRows.find(route => route.id === id);
    if (!previous || previous.routeType === newType) return;
    setRouteRows(current => current.map(route => (route.id === id ? { ...route, routeType: newType } : route)));
    try {
      const updated = await updateRouteType(id, location.id, newType, requestedBy);
      setRouteRows(current => current.map(route => (route.id === id ? updated : route)));
      toast.showToast({ header: 'Success', subtext: 'Route type updated.', variant: 'success' });
    } catch {
      setRouteRows(current => current.map(route => (route.id === id ? previous : route)));
      toast.showToast({ header: 'Error', subtext: 'Failed to update route type.', variant: 'error' });
    }
  }, [isProfileMode, location?.id, requestedBy, routeRows, toast]);

  const handleRouteActiveToggle = useCallback(async (id: number) => {
    if (isProfileMode || !location?.id) return;
    const previous = routeRows.find(route => route.id === id);
    if (!previous) return;
    const nextValue = !previous.isActive;
    setRouteRows(current => current.map(route => (route.id === id ? { ...route, isActive: nextValue } : route)));
    try {
      const updated = await toggleRouteActive(id, location.id, nextValue, requestedBy);
      setRouteRows(current => current.map(route => (route.id === id ? updated : route)));
      toast.showToast({
        header: 'Success',
        subtext: `Route ${nextValue ? 'activated' : 'deactivated'}.`,
        variant: 'success',
      });
    } catch {
      setRouteRows(current => current.map(route => (route.id === id ? previous : route)));
      toast.showToast({ header: 'Error', subtext: 'Failed to update route status.', variant: 'error' });
    }
  }, [isProfileMode, location?.id, requestedBy, routeRows, toast]);

  const routeColumns = useMemo(() => ([
    {
      columnKey: 'routeNumber',
      header: 'Route Number',
      flexGrow: 1.5,
      sortable: true,
    },
    {
      columnKey: 'routeType',
      header: 'Route Type',
      flexGrow: 1.8,
      sortable: true,
      filterOptions: routeTypeOptions,
      renderCell: (route: ManagedRoute) => (
        <LocationFormSelect
          options={routeTypeOptions.map(option => option.value)}
          value={route.routeType ?? ''}
          onChange={value => handleRouteTypeChange(route.id, value)}
          placeholder="Select route type..."
          disabled={isProfileMode}
        />
      ),
    },
    {
      columnKey: 'isActive',
      header: 'Active',
      flexGrow: 1,
      sortable: false,
      filterOptions: [
        { label: 'Active', value: 'true' },
        { label: 'Inactive', value: 'false' },
      ],
      renderCell: (route: ManagedRoute) => (
        <TouchableOpacity onPress={() => handleRouteActiveToggle(route.id)} activeOpacity={0.8} disabled={isProfileMode}>
          <View style={[panelStyles.routeToggle, route.isActive ? panelStyles.routeToggleOn : panelStyles.routeToggleOff]}>
            <View style={[panelStyles.routeThumb, route.isActive ? panelStyles.routeThumbOn : panelStyles.routeThumbOff]} />
          </View>
        </TouchableOpacity>
      ),
    },
  ]), [handleRouteActiveToggle, handleRouteTypeChange, routeTypeOptions]);

  const renderRoutesSection = useCallback((context: 'profile' | 'edit') => {
    const hasFilters = !!routeSearch || Object.values(routeColumnFilters).some(values => values.length > 0);
    const routeCountText = routeTotalCount === 1 ? 'route' : 'routes';
    const routeTableDescription = routeTotalCount > 0
      ? `Showing ${routePage * routeLoadParams.size + 1}–${Math.min((routePage + 1) * routeLoadParams.size, routeTotalCount)} of ${routeTotalCount} ${routeCountText}`
      : '';

    return (
      <View style={[profileStyles.detailSection, isDark && { borderBottomColor: colors.border.divider }]}>
        <View style={profileStyles.subtableHead}>
          <View>
            <Text style={[profileStyles.subtableTitle, isDark && { color: colors.text.primary }]}>Route Management</Text>
            <Text style={[profileStyles.subtableSubtitle, isDark && { color: colors.text.secondary }]}>
              {routeTotalCount} {routeCountText} configured{(displayLocation ?? location)?.name ? ` for ${(displayLocation ?? location)?.name}` : ''}.
            </Text>
          </View>
          <View style={profileStyles.duoRow}>
            <Text style={[profileStyles.duoLabel, isDark && { color: colors.text.primary }]}>DUO Status</Text>
            <Switch
              value={duoState}
              onValueChange={handleToggleDuo}
              disabled={isProfileMode || !location?.id}
              trackColor={{ false: '#BBC3CC', true: '#005CBC' }}
              thumbColor={Platform.OS === 'android' ? '#fff' : undefined}
              ios_backgroundColor="#BBC3CC"
            />
            <Text style={[profileStyles.duoStatus, isDark && { color: colors.text.secondary }]}>{duoState ? 'Enabled' : 'Disabled'}</Text>
          </View>
        </View>

        <View style={panelStyles.routeToolbar}>
          <View style={panelStyles.routeToolbarLeft}>
            <View style={[panelStyles.routeSearchBox, isDark && { backgroundColor: colors.input.background, borderColor: colors.input.border }]}>
              <MaterialIcons name="search" size={16} color={isDark ? colors.text.secondary : AdminColors.textMuted} style={panelStyles.routeSearchIcon} />
              <TextInput
                style={[panelStyles.routeSearchInput, isDark && { color: colors.text.primary }]}
                placeholder="Search routes..."
                placeholderTextColor={isDark ? colors.text.disabled : AdminColors.textMuted}
                value={routeSearch}
                onChangeText={handleRouteSearchChange}
              />
              {!!routeSearch && (
                <TouchableOpacity onPress={() => handleRouteSearchChange('')} activeOpacity={0.7}>
                  <MaterialIcons name="close" size={14} color={isDark ? colors.text.secondary : AdminColors.textMuted} />
                </TouchableOpacity>
              )}
            </View>
            <Text style={[panelStyles.routeCountText, isDark && { color: colors.text.secondary }]}>{routeTotalCount} {routeCountText}</Text>
            {hasFilters && (
              <AdminButton label="Clear Filters" variant="secondary" small onPress={handleClearRouteFilters} />
            )}
          </View>
          <View style={panelStyles.routeToolbarActions}>
            <AdminButton label="Bulk Add" variant="secondary" small disabled={isProfileMode} onPress={() => setShowBulkAddRoutesModal(true)} />
            <AdminButton label="+ Add Route" variant="primary" small disabled={isProfileMode} onPress={() => setShowAddRouteModal(true)} />
          </View>
        </View>

        {!!routeError && (
          <View style={panelStyles.routeErrorBanner}>
            <Text style={panelStyles.routeErrorText}>{routeError}</Text>
          </View>
        )}

        <RouteDataTable
          data={routeRows}
          columns={routeColumns}
          tableHeader=""
          tableDescription={routeTableDescription}
          showHeaderTopBorder={false}
          onRowClick={() => undefined}
          isLoading={routeIsLoading}
          maxRecordsToShow={routeLoadParams.size}
          externalPage={routePage}
          externalTotalPages={routeTotalPages}
          onExternalPageChange={(newPage: number) => setRouteLoadParams(prev => ({ ...prev, page: newPage }))}
          onPageSizeChange={(newSize: number) => setRouteLoadParams(prev => ({ ...prev, size: newSize, page: 0 }))}
          onSort={handleRouteSortChange}
          isFirstPage={routeIsFirst}
          isLastPage={routeIsLast}
          columnFilters={routeColumnFilters}
          onColumnFilter={handleRouteColumnFilter}
        />

        {routeRows.length === 0 && !routeIsLoading && !routeError && (
          <View style={profileStyles.emptyState}>
            <Text style={[profileStyles.emptyStateText, isDark && { color: colors.text.secondary }]}>No routes configured for this location.</Text>
          </View>
        )}
      </View>
    );
  }, [displayLocation, isDark, colors, duoState, handleClearRouteFilters, handleRouteColumnFilter, handleRouteSearchChange, handleRouteSortChange, handleToggleDuo, isProfileMode, location?.name, routeColumnFilters, routeError, routeIsLoading, routeLoadParams.size, routePage, routeRows, routeSearch, routeTotalCount, routeTotalPages, routeIsFirst, routeIsLast]);

  const renderFormField = useCallback((field: FieldDef) => {
    const uniquenessError = field.key === 'id' || field.key === 'sap' ? uniquenessErrors[field.key as UniqueField] : undefined;
    const error = errors[field.key] ?? uniquenessError;
    const sourceForm = isProfileMode && location ? locationToForm(location) : form;
    const value = String(sourceForm[field.key] ?? '');
    const isLocked = Boolean(
      isProfileMode ||
      (field.key === 'pickedVolume' && !isAddMode) ||
      (isEditMode && field.readonlyEdit) ||
      (isEditMode && field.edfLocked && resolvedEdfSynced),
    );
    const placeholder = isBulkMode ? 'Keep current value...' : field.placeholder;
    const wrapStyle = [panelStyles.formFieldWrap, field.span === 2 && panelStyles.formFieldSpan2];
    const labelSuffix = field.optional ? 'Optional' : undefined;
    const showEdfBadge = (isEditMode || isProfileMode) && field.edfLocked && resolvedEdfSynced;

    if (field.options === 'users') {
      const users = field.key === 'marketLead' || field.key === 'regionLead' ? marketLeadUsers : coeResourceUsers;
      return (
        <View key={field.key} style={wrapStyle}>
          <AdminFormField label={field.label} labelSuffix={labelSuffix} error={error}>
            {showEdfBadge ? (
              <View style={panelStyles.edfBadge}>
                <Text style={panelStyles.edfBadgeText}>EDF</Text>
              </View>
            ) : null}
            <LocationUserSearchSelect users={users} value={value === KEEP_VALUE ? '' : value} onChange={next => handleFieldChange(field.key, next)} placeholder={placeholder ?? 'Search Navigator users...'} hasError={Boolean(error)} disabled={isLocked} />
          </AdminFormField>
        </View>
      );
    }

    if (field.options === 'regions') {
      return (
        <View key={field.key} style={wrapStyle}>
          <AdminFormField label={field.label} required={field.required} labelSuffix={labelSuffix} error={error}>
            {showEdfBadge ? (
              <View style={panelStyles.edfBadge}>
                <Text style={panelStyles.edfBadgeText}>EDF</Text>
              </View>
            ) : null}
            <LocationFormSelect options={regions} value={value === KEEP_VALUE ? '' : value} onChange={next => handleFieldChange(field.key, next)} placeholder={placeholder ?? 'Select region...'} hasError={Boolean(error)} disabled={isLocked} />
          </AdminFormField>
        </View>
      );
    }

    if (field.options === 'markets') {
      return (
        <View key={field.key} style={wrapStyle}>
          <AdminFormField label={field.label} required={field.required} labelSuffix={labelSuffix} error={error}>
            {showEdfBadge ? (
              <View style={panelStyles.edfBadge}>
                <Text style={panelStyles.edfBadgeText}>EDF</Text>
              </View>
            ) : null}
            <LocationFormSelect
              options={markets}
              value={value === KEEP_VALUE ? '' : value}
              onChange={next => handleFieldChange(field.key, next)}
              placeholder={!form.region ? 'Select region first...' : (placeholder ?? 'Select market...')}
              hasError={Boolean(error)}
              disabled={isLocked || !form.region}
            />
          </AdminFormField>
        </View>
      );
    }

    if (Array.isArray(field.options)) {
      return (
        <View key={field.key} style={wrapStyle}>
          <AdminFormField label={field.label} required={field.required} labelSuffix={labelSuffix} error={error}>
            {showEdfBadge ? (
              <View style={panelStyles.edfBadge}>
                <Text style={panelStyles.edfBadgeText}>EDF</Text>
              </View>
            ) : null}
            <LocationFormSelect options={field.options} value={value === KEEP_VALUE ? '' : value} onChange={next => handleFieldChange(field.key, next)} placeholder={placeholder ?? 'Select...'} hasError={Boolean(error)} disabled={isLocked} />
          </AdminFormField>
        </View>
      );
    }

    return (
      <View key={field.key} style={wrapStyle}>
        <AdminFormField label={field.label} required={field.required} labelSuffix={labelSuffix} error={error}>
          {showEdfBadge ? (
            <View style={panelStyles.edfBadge}>
              <Text style={panelStyles.edfBadgeText}>EDF</Text>
            </View>
          ) : null}
          <AdminTextInput
            value={value === KEEP_VALUE ? '' : value}
            onChangeText={next => {
              if (field.type === 'integer' && !/^\d*$/.test(next)) return;
              handleFieldChange(field.key, next);
            }}
            onBlur={field.key === 'id' || field.key === 'sap' ? () => { void handleUniqueFieldBlur(field.key as UniqueField); } : undefined}
            placeholder={placeholder ?? ''}
            keyboardType={field.type === 'integer' ? 'number-pad' : field.type === 'number' ? 'decimal-pad' : 'default'}
            readOnly={isLocked}
            hasError={Boolean(error)}
            maxLength={field.max}
          />
        </AdminFormField>
      </View>
    );
  }, [coeResourceUsers, errors, form, form.region, handleFieldChange, isBulkMode, isEditMode, marketLeadUsers, markets, regions, resolvedEdfSynced]);

  const renderFormSection = useCallback((section: SectionDef) => {
    const collapsed = formCollapsed[section.key] ?? false;
    return (
      <View key={section.key} style={[panelStyles.formSection, isDark && { borderColor: colors.border.divider }]}>
        <TouchableOpacity style={[panelStyles.formSectionHeader, isDark && { backgroundColor: colors.background.elevated, borderBottomColor: colors.border.divider }]} onPress={() => handleToggleSection(section.key)} activeOpacity={0.8}>
          <Text style={[panelStyles.formSectionTitle, isDark && { color: colors.text.primary }]}>{section.title}</Text>
          <Text style={[panelStyles.formSectionChevron, isDark && { color: colors.text.secondary }]}>{collapsed ? '▸' : '▾'}</Text>
        </TouchableOpacity>
        {!collapsed && (
          <View style={[panelStyles.formSectionBody, isDark && { backgroundColor: colors.background.surface }]}>
            <View style={panelStyles.formGrid}>{section.fields.map(renderFormField)}</View>
            {section.key === 'primary' && isEditMode && form.status === 'Disabled' && initialForm.status !== 'Disabled' && activeEmployees.length > 0 && (
              <AdminInfoBanner
                variant="warn"
                message={<Text><Text style={{ fontFamily: FontFamily.bold }}>{activeEmployees.length} active {activeEmployees.length === 1 ? 'employee' : 'employees'}</Text>{' '}are currently assigned to this location. Disabling will restrict their access.</Text>}
              />
            )}
          </View>
        )}
      </View>
    );
  }, [activeEmployees.length, formCollapsed, handleToggleSection, isDark, colors, initialForm.status, isEditMode, renderFormField, form.status]);

  const renderTabBar = (activeTab: LocationTabId, onChange: (tab: LocationTabId) => void) => (
    <View style={[profileStyles.modalTabsContainer, isDark && { backgroundColor: colors.background.surface, borderBottomColor: colors.border.default }]}>
      <View style={profileStyles.modalTabs}>
        {LOCATION_TABS.map(tab => (
          <TouchableOpacity
            key={tab.id}
            style={[
              profileStyles.modalTab,
              activeTab === tab.id && profileStyles.modalTabActive,
              isDark && !( activeTab === tab.id) && { borderBottomColor: 'transparent' },
              isDarkAny && activeTab === tab.id && { backgroundColor: '#0d2a44', borderWidth: 1, borderColor: '#3D8BFF' },
            ]}
            onPress={() => onChange(tab.id)}
            activeOpacity={0.8}
          >
            <Text
              {...(isDarkAny && activeTab === tab.id ? { dataSet: { lhKeepColor: '1' } } as any : {})}
              style={[
                profileStyles.modalTabText,
                activeTab === tab.id && profileStyles.modalTabTextActive,
                isDark && !( activeTab === tab.id) && { color: colors.text.secondary },
                isDarkAny && activeTab === tab.id && { color: '#6AADFF' },
              ]}
            >
              {tab.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  const renderProfileContent = () => {
    return (
      <>
        {renderTabBar(activeProfileTab, setActiveProfileTab)}

        <ScrollView style={[profileStyles.modalBody, isDark && { backgroundColor: colors.background.page }]} contentContainerStyle={profileStyles.modalBodyContent} showsVerticalScrollIndicator>
          {activeProfileTab === 'information' && (
            <>
              {resolvedEdfSynced && (
                <AdminInfoBanner
                  variant="info"
                  message={edfSyncTimestamp ? `This location is managed by EDF. Governed fields are locked and cannot be edited here. Last successful EDF sync: ${edfSyncTimestamp}.` : 'This location is managed by EDF. Governed fields are locked and cannot be edited here.'}
                />
              )}

              <View style={panelStyles.masterRow}>
                <TouchableOpacity onPress={handleToggleAll}>
                  <Text style={[panelStyles.masterButton, isDark && { color: colors.brand.primary }]}>{profileSections.every(section => (profileCollapsed[section.key] ?? false)) ? 'Expand All' : 'Collapse All'}</Text>
                </TouchableOpacity>
              </View>

              {profileSections.map(renderProfileSection)}
            </>
          )}

          {activeProfileTab === 'employees' && renderEmployeesSection('profile')}
          {activeProfileTab === 'routes' && renderRoutesSection('profile')}
        </ScrollView>

        <View style={[profileStyles.modalFooter, isDark && { borderTopColor: colors.border.default, backgroundColor: colors.background.surface }]}>
          {onBack ? (
            <TouchableOpacity style={profileStyles.footerBtn} onPress={onBack}>
              <Text style={[profileStyles.footerBtnText, isDark && { color: colors.brand.primary }]}>← Back to Location Management</Text>
            </TouchableOpacity>
          ) : null}
          <AdminButton label="Close" variant="secondary" onPress={onClose} small />
          {!hideEditLocation && (
            <AdminButton
              label="Edit Location"
              variant="primary"
              small
              onPress={() => {
                if (onModeChange) onModeChange('edit');
                else onEditLocation?.();
              }}
            />
          )}
        </View>
      </>
    );
  };

  const renderEditContent = () => (
    <>
      {renderTabBar(activeEditFormTab, setActiveEditFormTab)}

      <ScrollView style={[profileStyles.modalBody, isDark && { backgroundColor: colors.background.page }]} contentContainerStyle={profileStyles.modalBodyContent} keyboardShouldPersistTaps="handled">
        {activeEditFormTab === 'information' && (
          <>
            {resolvedEdfSynced && (
              <AdminInfoBanner variant="info" message="This location is managed by EDF. Governed fields are locked and cannot be edited here." />
            )}
            {validationServiceError ? (
              <View style={panelStyles.errorBanner}>
                <Text style={panelStyles.errorText}>{validationServiceError}</Text>
              </View>
            ) : null}
            {apiError ? (
              <View style={panelStyles.errorBanner}>
                <Text style={panelStyles.errorText}>{apiError}</Text>
              </View>
            ) : null}

            <View style={panelStyles.masterRow}>
              <TouchableOpacity onPress={handleToggleAll}>
                <Text style={[panelStyles.masterButton, isDark && { color: colors.brand.primary }]}>{displayedFormSections.every(section => (formCollapsed[section.key] ?? false)) ? 'Expand All' : 'Collapse All'}</Text>
              </TouchableOpacity>
            </View>

            {displayedFormSections.map(renderFormSection)}
          </>
        )}

        {activeEditFormTab === 'employees' && renderEmployeesSection('edit')}
        {activeEditFormTab === 'routes' && renderRoutesSection('edit')}
      </ScrollView>

      <View style={[profileStyles.modalFooter, isDark && { borderTopColor: colors.border.default, backgroundColor: colors.background.surface }]}>
        <AdminButton label={formIsDirty ? 'Cancel' : 'Close'} variant="secondary" onPress={requestClose} small />
        <AdminButton
          label={saving ? 'Saving...' : isAddMode ? 'Save' : isBulkMode ? 'Apply Changes' : 'Save Changes'}
          variant="primary"
          onPress={handleSave}
          disabled={saving}
          loading={saving}
          small
        />
      </View>
    </>
  );

  const renderFormContent = () => {
    if (isProfileMode) {
      return renderProfileContent();
    }

    if (isEditMode) {
      return renderEditContent();
    }

    return (
      <>
        <ScrollView style={[profileStyles.modalBody, isDark && { backgroundColor: colors.background.page }]} contentContainerStyle={profileStyles.modalBodyContent} keyboardShouldPersistTaps="handled">
          {isBulkMode && bulkHasSynced && (
            <AdminInfoBanner variant="info" message="One or more selected locations are EDF-synced. Governed fields are excluded and will not be changed." />
          )}
          {validationServiceError ? (
            <View style={panelStyles.errorBanner}>
              <Text style={panelStyles.errorText}>{validationServiceError}</Text>
            </View>
          ) : null}
          {isBulkMode && (
            <View style={[panelStyles.bulkNote, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}>
              <Text style={[panelStyles.bulkNoteText, isDark && { color: colors.text.secondary }]}>
                <Text style={{ fontFamily: FontFamily.bold }}>{selectedLocations.length} locations selected.</Text>{' '}
                Only fields you populate below will be changed.
              </Text>
            </View>
          )}
          {apiError ? (
            <View style={panelStyles.errorBanner}>
              <Text style={panelStyles.errorText}>{apiError}</Text>
            </View>
          ) : null}

          <View style={panelStyles.masterRow}>
            <TouchableOpacity onPress={handleToggleAll}>
              <Text style={[panelStyles.masterButton, isDark && { color: colors.brand.primary }]}>{displayedFormSections.every(section => (formCollapsed[section.key] ?? false)) ? 'Expand All' : 'Collapse All'}</Text>
            </TouchableOpacity>
          </View>

          {displayedFormSections.map(renderFormSection)}
        </ScrollView>

        <View style={[profileStyles.modalFooter, isDark && { borderTopColor: colors.border.default, backgroundColor: colors.background.surface }]}>
          <AdminButton label={formIsDirty ? 'Cancel' : 'Close'} variant="secondary" onPress={requestClose} small />
          <AdminButton
            label={saving ? 'Saving...' : isAddMode ? 'Save' : isBulkMode ? 'Apply Changes' : 'Save Changes'}
            variant="primary"
            onPress={handleSave}
            disabled={saving}
            loading={saving}
            small
          />
        </View>
      </>
    );
  };

  if (!visible) return null;
  if (isProfileMode && !(displayLocation ?? location)) return null;

  const panelTranslateX = slideAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [panelWidth, 0],
  });

  return (
    <>
      <Modal
        visible={visible}
        animationType="none"
        transparent
        presentationStyle="overFullScreen"
        onRequestClose={requestClose}
      >
        <View style={[panelStyles.panelBackdrop, isDark && { backgroundColor: 'rgba(0,0,0,0.7)' }]}>
          <Animated.View
            style={[
              profileStyles.modalContainer,
              panelStyles.panelShell,
              isDark && { backgroundColor: colors.background.surface },
              {
                width: panelWidth,
                height: viewportHeight,
                transform: [{ translateX: panelTranslateX }],
              },
            ]}
          >
            <View style={[profileStyles.modalHeader, isDark && { backgroundColor: colors.background.elevated, borderBottomColor: colors.border.default }]}>
              <View style={profileStyles.modalHeaderContent}>
                <Text style={[profileStyles.modalTitle, isDark && { color: colors.text.primary }]}>{isProfileMode ? 'Location Profile' : isAddMode ? 'Add Location' : isBulkMode ? `Bulk Edit ${selectedLocations.length} Locations` : `Edit Location: ${location?.name ?? ''}`}</Text>
                <Text style={[profileStyles.modalSubtitle, isDark && { color: colors.text.secondary }]}>
                  {isProfileMode
                    ? `${(displayLocation ?? location)?.name ?? ''} · Location ID ${(displayLocation ?? location)?.id ?? ''}${(displayLocation ?? location)?.sap ? ` · SAP ID ${(displayLocation ?? location)?.sap}` : ''}`
                    : isAddMode
                      ? 'Create a governed Location Management record.'
                      : isBulkMode
                        ? 'Apply shared values across the selected location records.'
                        : `Update governed fields for ${location?.name ?? ''}.`}
                </Text>
                {isProfileMode && resolvedEdfSynced && edfSyncTimestamp ? (
                  <Text style={[profileStyles.modalSubtitle, isDark && { color: colors.text.secondary }]}>Last successful EDF sync: {edfSyncTimestamp}</Text>
                ) : null}
                {isProfileMode ? (
                  <View style={profileStyles.modalHeaderActionsRow}>
                    <TouchableOpacity
                      onPress={() => onShowOnMap?.()}
                      disabled={!onShowOnMap || !hasMapCoordinates}
                      activeOpacity={0.75}
                      style={[
                        profileStyles.headerActionButton,
                        (!onShowOnMap || !hasMapCoordinates) && profileStyles.headerActionButtonDisabled,
                      ]}
                    >
                      <MaterialIcons
                        name="map"
                        size={14}
                        color={!onShowOnMap || !hasMapCoordinates ? AdminColors.textMuted : AdminColors.blue800}
                      />
                      <Text
                        style={[
                          profileStyles.headerActionButtonText,
                          (!onShowOnMap || !hasMapCoordinates) && profileStyles.headerActionButtonTextDisabled,
                        ]}
                      >
                        Show on Map
                      </Text>
                    </TouchableOpacity>
                  </View>
                ) : null}
              </View>
              <View style={profileStyles.modalHeaderRight}>
                {resolvedEdfSynced && (
                  <View style={profileStyles.syncTagModal}>
                    <Text style={profileStyles.syncTagModalText}>Synced</Text>
                  </View>
                )}
                <TouchableOpacity onPress={requestClose} style={[profileStyles.modalCloseBtn, isDark && { backgroundColor: colors.background.surface }]}>
                  <Text style={[profileStyles.modalCloseBtnText, isDark && { color: colors.text.secondary }]}>×</Text>
                </TouchableOpacity>
              </View>
            </View>

            {renderFormContent()}
          </Animated.View>
        </View>
      </Modal>

      {location?.id && location?.name && (
        <>
          <BulkAddRoutesModal
            visible={showBulkAddRoutesModal}
            onClose={() => setShowBulkAddRoutesModal(false)}
            locationId={location.id}
            locationName={location.name}
            routeTypeOptions={routeTypeOptions}
            onSuccess={() => {
              setShowBulkAddRoutesModal(false);
              refreshRoutes();
            }}
          />

          <AddRouteModal
            visible={showAddRouteModal}
            onClose={() => setShowAddRouteModal(false)}
            locationId={location.id}
            locationName={location.name}
            onSuccess={() => {
              setShowAddRouteModal(false);
              refreshRoutes();
            }}
            routeTypeOptions={routeTypeOptions}
          />
        </>
      )}

      <Modal visible={showDiscard} transparent animationType="fade" onRequestClose={() => setShowDiscard(false)}>
        <View style={confirmStyles.overlay}>
          <View style={[confirmStyles.box, isDark && { backgroundColor: colors.background.elevated }]}>
            <Text style={[confirmStyles.title, isDark && { color: colors.text.primary }]}>Discard Changes?</Text>
            <Text style={[confirmStyles.message, isDark && { color: colors.text.secondary }]}>You have unsaved changes. Are you sure you want to discard them?</Text>
            <View style={[confirmStyles.footer, isDark && { borderTopColor: colors.border.default }]}>
              <AdminButton label="Keep Editing" variant="secondary" onPress={() => setShowDiscard(false)} />
              <AdminButton label="Discard" variant="primary" onPress={() => { setShowDiscard(false); onClose(); }} />
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={showDisableConfirm} transparent animationType="fade" onRequestClose={() => setShowDisableConfirm(false)}>
        <View style={confirmStyles.overlay}>
          <View style={[confirmStyles.box, isDark && { backgroundColor: colors.background.elevated }]}>
            <Text style={[confirmStyles.title, isDark && { color: colors.text.primary }]}>Disable Location?</Text>
            <Text style={[confirmStyles.message, isDark && { color: colors.text.secondary }]}>
              {activeEmployees.length > 0
                ? `${activeEmployees.length} active ${activeEmployees.length === 1 ? 'employee' : 'employees'} are currently assigned. Disabling will restrict their access. Proceed?`
                : 'Are you sure you want to disable this location?'}
            </Text>
            <View style={[confirmStyles.footer, isDark && { borderTopColor: colors.border.default }]}>
              <AdminButton label="Cancel" variant="secondary" onPress={() => setShowDisableConfirm(false)} />
              <AdminButton label={saving ? 'Disabling...' : 'Disable'} variant="primary" onPress={handleConfirmDisable} />
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
};

export default LocationManagementModal;